import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import {
    ACTIONS,
    ADMIN_VERIFY_APPLICATION_URL,
    get_headers,
    GET_TABS_DETAILS,
    get_user_role,
    QUESTION_TYPES,
    REQUEST_INFO_URL,
    SUBMIT_APPLICATION_FORMS_ANSWERS,
    VERIFY_SECTION
} from "@/utils/constants/constants";
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
    validate_non_empty_string,
    validate_non_sub_zero_number
} from '@/utils/validations/validate_inputs';
import axios from "axios";

export default {
    name: "RenderSections",
    mixins: [alert_mixin],
    components: { FilePreviewComponent },
    emits: ['files-saved'],
    props: {
        form_main_section_id: { type: [String, Number], default: null },
        application_id: { type: [String, Number], default: null },
        application_form_id: { type: [String, Number], default: null },
        entity_profile_id: { type: [String, Number], default: null },
        current_status_id: { type: [String, Number], default: null },
    },

    data: () => ({
        isLoading: false,
        safeFormConfig: [],
        safeForms: [],
        open_map: {},
        currentFormIndex: 0,
        showFilePreviewModal: false,
        request_info_note: "",
        answered_file_questions: [],
        requested_field_ids: [],
        userRole: null,
        selected_request_fields: [],
        preview_modal: false,
        selected_url: null,
        allowed_actions: [],
        QUESTION_TYPES,
        ACTIONS
    }),

    computed: {
        progressPercentage() {
            const total = this.safeFormConfig.length || 1;
            const pct = Math.round(((this.currentFormIndex + 1) / total) * 100);
            return `${pct}%`;
        },
        is_verify_allowed() {
            return this.allowed_actions.some(action =>
                action.conditions_met === true &&
                action.action.includes(ACTIONS.VERIFY)
            );
        },

        is_request_information_allowed() {
            return this.allowed_actions.some(action =>
                action.conditions_met === true &&
                action.action.includes(ACTIONS.REQUEST_INFORMATION)
            );
        },

        can_submit() {
            const currentForm = this.get_safe_form_config(this.currentFormIndex);
            if (currentForm && this.hasAnyWriteAccess(currentForm)) {
                return true;
            }
            return this.is_submit_allowed;
        },

        is_entity_user() {
            return this.userRole && this.userRole.toLowerCase() === 'entity';
        },
        hasAnySectionAccess() {
            if (!Array.isArray(this.safeFormConfig) || this.safeFormConfig.length === 0) {
                return false;
            }
            return this.safeFormConfig.some(form =>
                Array.isArray(form.sections) && form.sections.some(section => this.hasSectionAccess(section))
            );
        }
    },

    watch: {
        form_main_section_id() { this.load_tabs_details(); },
        application_id() { this.load_tabs_details(); },
        application_form_id() { this.load_tabs_details(); },
        current_status_id() { this.load_tabs_details(); },
    },

    mounted() {
        this.load_tabs_details();
        this.headers = get_headers();
        this.userRole = get_user_role();
        const el = document.getElementById('request_information_modal');
        if (el) {
            el.addEventListener('show.bs.modal', () => {
                this.reset_request_information_state();
            });
            el.addEventListener('hidden.bs.modal', () => {
                this.reset_request_information_state();
            });
        }
    },

    methods: {
        handleNumberInput(event, field, fIdx) {
            const value = event.target.value.replace(/\D/g, '')
            Reflect.set(this.safeForms.at(fIdx), field.model, value)
        },
        
        hasSectionAccess(section) {
            if (this.is_entity_user) {
                return true;
            }
            return section.access &&
                section.access.action_name &&
                section.access.action_name !== ACTIONS.NO_ACCESS;
        },

        hasWriteAccess(section) {
            if (this.is_entity_user) {
                return true;
            }
            return section.access &&
                section.access.action_name === ACTIONS.WRITE;
        },

        hasReadAccess(section) {
            if (this.is_entity_user) {
                return false;
            }
            return section.access &&
                section.access.action_name === ACTIONS.READ;
        },

        hasAnyWriteAccess(form) {
            // If user is entity, always return true
            if (this.is_entity_user) {
                return true;
            }
            return form.sections && form.sections.some(section =>
                this.hasSectionAccess(section) && this.hasWriteAccess(section)
            );
        },

        get_safe_form(field_index) {
            if (!Array.isArray(this.safeForms)) {
                return null;
            }
            if (Reflect.has(this.safeForms, field_index)) {
                return Reflect.get(this.safeForms, field_index);
            }
            return null;
        },

        setSafeForm(field_index, value) {
            if (!Array.isArray(this.safeForms)) {
                this.safeForms = [];
            }
            return Reflect.set(this.safeForms, field_index, value);
        },

        get_safe_form_config(field_index) {
            if (!Array.isArray(this.safeFormConfig)) {
                return null;
            }
            if (Reflect.has(this.safeFormConfig, field_index)) {
                return Reflect.get(this.safeFormConfig, field_index);
            }
            return null;
        },

        file_exitsts(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return false;

            const v = safeForm[field.model];
            return !!(v && (v.original_url && v.preview_url));
        },

        open_file_preview(field_index, field) {
            this.selected_url = this.file_url(field_index, field);
            this.preview_modal = true;
        },

        file_url(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return "";

            const v = safeForm[field.model] || {};
            return typeof v === "string" ? v : (v.preview_url);
        },

        close_the_file_preview() {
            this.preview_modal = false;
            this.selected_url = null;
        },

        file_name(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return "";

            const v = safeForm[field.model];
            const named = v?.original_name || v?.name || v?.file_name;
            if (named) return named;

            const url = this.file_url(field_index, field);
            if (!url) return "";

            try {
                const u = new URL(url);
                return decodeURIComponent((u.pathname.split("/").pop() || "").split("?")[0]);
            } catch {
                const last = (url.split("?")[0].split("/").pop() || "");
                try { return decodeURIComponent(last); } catch { return last; }
            }
        },

        handle_file_upload(e, field, field_index = this.currentFormIndex) {
            const file = e?.target?.files?.[0];
            if (!file) return;

            const preview = URL.createObjectURL(file);
            const cur = this.get_safe_form(field_index) || {};

            this.setSafeForm(field_index, {
                ...cur,
                [field.model]: {
                    file,
                    file_name: file.name,
                    size: file.size,
                    type: file.type,
                    preview_url: preview,
                    original_url: null
                },
            });
        },

        remove_file(field_index, model) {
            const cur = this.get_safe_form(field_index) || {};
            const existing = Reflect.get(cur, model);

            if (existing?.preview_url) {
                URL.revokeObjectURL(existing.preview_url);
            }

            const updated = { ...cur, [model]: null };
            this.setSafeForm(field_index, updated);
            const reasonKey = `${model}_file_comment`;
            if (Reflect.has(updated, reasonKey)) {
                Reflect.set(updated, reasonKey, "");
            }
        },

        get_file_url(v) {
            if (!v) return "";
            return typeof v === "string"
                ? v
                : v.presigned_url || v.original_url || v.preview_url || "";
        },

        get_file_name(v) {
            if (!v) return "";
            const named = v?.original_name || v?.name || v?.file_name;
            if (named) return named;

            const url = this.get_file_url(v);
            if (!url) return "";

            try {
                const { pathname } = new URL(url);
                const last = pathname.split("/").pop() || "";
                return decodeURIComponent(last);
            } catch (e) {
                const noQuery = url.split("?")[0];
                const last = noQuery.split("/").pop() || "";
                try { return decodeURIComponent(last); } catch { return last; }
            }
        },

        load_tabs_details() {
            this.isLoading = true;

            const request_params = {
                form_main_section_id: this.form_main_section_id != null ? Number(this.form_main_section_id) : null,
                application_form_id: this.application_form_id != null ? Number(this.application_form_id) : null,
                application_id: this.application_id != null ? Number(this.application_id) : null,
                entity_profile_id: this.entity_profile_id != null ? Number(this.entity_profile_id) : null,
                current_status_id: this.current_status_id != null ? Number(this.current_status_id) : null,
            };

            return axios
                .get(GET_TABS_DETAILS, { headers: get_headers(), params: request_params })
                .then(({ data }) => {
                    if (data?.status !== "success" || !Array.isArray(data?.data)) {
                        this.safeFormConfig = [];
                        this.safeForms = [];
                        this.open_map = {};
                        return;
                    }

                    this._raw_api_data = data.data;
                    const form_config = this.build_form_config(data.data);
                    const safe_forms = this.init_safe_forms_from_config(form_config);

                    this.safeFormConfig = form_config;
                    this.safeForms = safe_forms;
                    this.allowed_actions = data.allowed_actions || [];
                    this.init_open_map();
                    this.restore_dependent_question_visibility();
                    return this.$nextTick();
                })
                .catch((e) => {
                    this.show_error?.(e?.response?.data?.message || e.message || "Failed to load tab details");
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        build_form_config(api_forms) {
            return api_forms.map((main) => {
                const sections = (main.sections || []).map((sec) => {
                    const fields = (sec.questions || []).map((q) => this.normalize_question_to_field(q, sec.section_id));
                    return {
                        id: sec.section_id ?? sec.id,
                        title: sec.section_name || "",
                        is_verified: sec.is_verified,
                        fields,
                        access: sec.access || {}
                    };
                });

                return {
                    _mainId: main.main_section_id ?? main.id,
                    formTitle: main.name || "",
                    sections,
                };
            });
        },

        normalize_question_to_field(q, section_id) {
            const model = `q_${q.question_id}`;

            const type_map = {
                short_text: QUESTION_TYPES.TEXT,
                long_text: QUESTION_TYPES.TEXTAREA,
                number: QUESTION_TYPES.NUMBER,
                date: QUESTION_TYPES.DATE,
                single_select: QUESTION_TYPES.SINGLE_SELECT,
                multi_select: QUESTION_TYPES.MULTI_SELECT,
                file: QUESTION_TYPES.FILE,
                facial_verification: QUESTION_TYPES.FACIAL_VERIFICATION
            };
            const type = type_map[q.question_type] || QUESTION_TYPES.TEXT;

            const pick_first = (...candidates) =>
                candidates.find(candidate => candidate !== undefined && candidate !== null);

            const get_option_text = (option, index) =>
                String(
                    pick_first(
                        option.choice,
                        option.text,
                        option.label,
                        option.value,
                        `Option ${index + 1}`
                    )
                );

            const get_option_value = (option, text_value) =>
                String(
                    pick_first(
                        option.choice,
                        option.value,
                        option.id,
                        text_value
                    )
                );

            const normalize_options = (options) => {
                if (!Array.isArray(options)) return [];
                return options.map((option, index) => {
                    const text = get_option_text(option, index);
                    const value = get_option_value(option, text);
                    return { value, text };
                });
            };

            const latest = q.latest_answer?.answer ?? null;
            const default_value = this.coerce_default_by_type(type, latest);

            const dependent_questions = (q.dependant_questions || []).map(dependent => {
                const dependent_type = this.map_question_type(dependent.question_type);
                const dependent_latest = dependent.latest_answer?.answer ?? null;
                const dependent_default = this.coerce_default_by_type(dependent_type, dependent_latest);
                const normalized_dependent = {
                    id: dependent.id,
                    question_id: dependent.question_id,
                    section_question_id: dependent.id,
                    label: dependent.question_text || "",
                    model: `dq_${dependent.question_id}`,
                    type: dependent_type,
                    required: !!dependent.is_mandatory,
                    trigger_condition: dependent.trigger_condition || "",
                    options: normalize_options(dependent.options),
                    __default: dependent_default,
                    is_visible: false
                };
                return normalized_dependent;
            });

            const normalized_question = {
                id: q.id,
                question_id: q.question_id,
                section_id,
                model,
                label: q.question_text || "",
                type,
                required: !!q.is_mandatory,
                options: normalize_options(q.options),
                __default: default_value,
                dependent_questions
            };
            return normalized_question;
        },

        map_question_type(type) {
            const type_map = new Map([
                ['short_text', QUESTION_TYPES.TEXT],
                ['long_text', QUESTION_TYPES.TEXTAREA],
                ['single_select', QUESTION_TYPES.SINGLE_SELECT],
                ['radio', QUESTION_TYPES.SINGLE_SELECT],
                ['multi_select', QUESTION_TYPES.MULTI_SELECT],
                ['checkboxes', QUESTION_TYPES.MULTI_SELECT],
                ['file', QUESTION_TYPES.FILE],
                ['number', QUESTION_TYPES.NUMBER],
                ['date', QUESTION_TYPES.DATE],
                ['facial_verification', QUESTION_TYPES.FACIAL_VERIFICATION]
            ]);

            return type_map.get(type) || QUESTION_TYPES.TEXT;
        },

        coerce_default_by_type(type, answer) {
            switch (type) {
                case QUESTION_TYPES.TEXT:
                case QUESTION_TYPES.TEXTAREA:
                case QUESTION_TYPES.DATE:
                    return typeof answer === "string" ? answer : "";
                case QUESTION_TYPES.NUMBER: {
                    return answer;
                }
                case QUESTION_TYPES.SINGLE_SELECT: {
                    if (Array.isArray(answer)) return answer.length ? String(answer[0]) : "";
                    return answer ? String(answer) : "";
                }
                case QUESTION_TYPES.MULTI_SELECT:
                    return Array.isArray(answer) ? answer.map((v) => String(v)) : [];
                case QUESTION_TYPES.FILE:
                case QUESTION_TYPES.FACIAL_VERIFICATION: {
                    let returned_answer = null;
                    if (typeof answer === "string" && answer) {
                        returned_answer = { original_url: answer, preview_url: answer };
                    }
                    if (answer && typeof answer === "object") {
                        returned_answer = {
                            original_url: answer.original_url,
                            preview_url: answer.presigned_url,
                            file_name: answer.file_name
                        };
                    }
                    return returned_answer;
                }
                default:
                    return typeof answer === "string" ? answer : "";
            }
        },

        init_safe_forms_from_config(config) {
            return config.map((form) => {
                const store = {};
                (form.sections || []).forEach((sec) => {
                    (sec.fields || []).forEach((f) => {
                        // Main question
                        store[f.model] = f.__default;

                        // Initialize dependent questions with their default values
                        if (f.dependent_questions && f.dependent_questions.length) {
                            f.dependent_questions.forEach(dq => {
                                store[dq.model] = dq.__default;
                            });
                        }
                    });
                });
                return store;
            });
        },

        restore_dependent_question_visibility() {
            this.safeFormConfig.forEach((form, field_index) => {
                (form.sections || []).forEach((section) => {
                    (section.fields || []).forEach((field) => {
                        if (field.dependent_questions && field.dependent_questions.length) {
                            const safeForm = this.get_safe_form(field_index);
                            const current_value = safeForm ? safeForm[field.model] : undefined;
                            field.dependent_questions.forEach(dq => {
                                const should_be_visible = this.does_value_match_trigger(
                                    current_value,
                                    dq.trigger_condition,
                                    field
                                );
                                dq.is_visible = should_be_visible;
                                if (should_be_visible && safeForm) {
                                    // The answer should already be loaded from init_safe_forms_from_config
                                    // but we can double-check here if needed
                                }
                            });
                        }
                    });
                });
            });
        },

        init_open_map() {
            this.open_map = {};
            this.safeFormConfig.forEach((form, field_index) => {
                Reflect.set(this.open_map, field_index, {});
                (form.sections || []).forEach((_, sIdx) => {
                    Reflect.set(Reflect.get(this.open_map, field_index), sIdx, true);
                });
            });
        },

        is_section_open(fIdx, sIdx) {
            if (!Number.isInteger(fIdx) || !Number.isInteger(sIdx) || fIdx < 0 || sIdx < 0) {
                return true;
            }

            if (!Reflect.has(this.open_map, fIdx) ||
                !(typeof Reflect.get(this.open_map, fIdx) === 'object' && Reflect.get(this.open_map, fIdx) !== null) ||
                typeof Reflect.get(Reflect.get(this.open_map, fIdx), sIdx) === 'undefined') {
                return true;
            }

            const form_entry = Reflect.get(this.open_map, fIdx);
            return typeof form_entry === 'object' && form_entry !== null && Reflect.has(form_entry, sIdx) ? Reflect.get(form_entry, sIdx) : true;
        },

        toggle_section(fIdx, sIdx) {
            if (!Number.isInteger(fIdx) || !Number.isInteger(sIdx) || fIdx < 0 || sIdx < 0) {
                return;
            }

            if (!Reflect.has(this.open_map, fIdx)) {
                Reflect.set(this.open_map, fIdx, {});
            }

            const form_entry = Reflect.get(this.open_map, fIdx);
            if (typeof form_entry !== 'object' || form_entry === null) {
                Reflect.set(this.open_map, fIdx, {});
            }

            const validated_form_entry = Reflect.get(this.open_map, fIdx);
            const current_value = this.is_section_open(fIdx, sIdx);
            Reflect.set(validated_form_entry, sIdx, !current_value);
        },

        is_empty(field, field_index) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return true;

            const val = safeForm[field.model] ?? field.__default;

            switch (field.type) {
                case QUESTION_TYPES.TEXT:
                case QUESTION_TYPES.TEXTAREA:
                case QUESTION_TYPES.DATE:
                    return !val || String(val).trim() === "";
                case QUESTION_TYPES.NUMBER:
                    return val === null || val === undefined || String(val) === "";
                case QUESTION_TYPES.SINGLE_SELECT:
                    return !val || String(val) === "";
                case QUESTION_TYPES.MULTI_SELECT:
                    return !Array.isArray(val) || val.length === 0;
                case QUESTION_TYPES.FILE:
                    return !val || !(val.presigned_url || val.original_url || val.preview_url);
                case QUESTION_TYPES.FACIAL_VERIFICATION:
                    return !val || !(val.presigned_url || val.original_url || val.preview_url);
                default:
                    return !val || String(val).trim() === "";
            }
        },

        next_form() {
            if (this.currentFormIndex < this.safeFormConfig.length - 1) {
                this.currentFormIndex += 1;
                this.$nextTick(() => {
                    this.restore_dependent_question_visibility_for_form(this.currentFormIndex);
                });
            }
        },

        prev_form() {
            if (this.currentFormIndex > 0) {
                this.currentFormIndex -= 1;
                this.$nextTick(() => {
                    this.restore_dependent_question_visibility_for_form(this.currentFormIndex);
                });
            }
        },

        restore_dependent_question_visibility_for_form(field_index) {
            const form = this.get_safe_form_config(field_index);
            if (!form || !form.sections) return;

            form.sections.forEach((section) => {
                (section.fields || []).forEach((field) => {
                    if (field.dependent_questions && field.dependent_questions.length) {
                        const safeForm = this.get_safe_form(field_index);
                        const current_value = safeForm ? safeForm[field.model] : undefined;

                        field.dependent_questions.forEach(dq => {
                            const should_be_visible = this.does_value_match_trigger(
                                current_value,
                                dq.trigger_condition,
                                field
                            );
                            dq.is_visible = should_be_visible;
                        });
                    }
                });
            });
        },

        populate_answered_file_questions(field_index, sIdx) {
            const form = this.get_safe_form_config(field_index);
            if (!form) return;

            const sections = Reflect.get(form, "sections");
            const section = sections ? Reflect.get(sections, sIdx) : undefined;
            const safeForm = this.get_safe_form(field_index);
            if (!section || !safeForm) return;

            this.answered_file_questions = (section.fields || [])
                .filter((f) => f.type === QUESTION_TYPES.FILE || f.type === QUESTION_TYPES.FACIAL_VERIFICATION)
                .filter((f) => {
                    const v = safeForm[f.model];
                    return v && (v.presigned_url || v.original_url || v.preview_url);
                })
                .map((f) => ({
                    section_question_id: f.id,
                    question_id: f.question_id,
                    section_id: f.section_id,
                    question_text: f.label,
                }));
        },

        toggle_requested_field(event, field) {
            if (event.target.checked) {
                this.selected_request_fields.push({
                    form_section_id: field.section_id,
                    question_id: field.question_id,
                    requested_field_id: field.section_question_id,
                    requested_field_text: field.question_text
                });
                return;
            }

            this.selected_request_fields = this.selected_request_fields.filter(
                selected => selected.requested_field_id !== field.section_question_id
            );
        },

        section_verification(section_id) {
            const application_form_id = this.application_form_id

            const data = {
                section_id: section_id,
                is_verified: true,
                application_form_id: parseInt(application_form_id, 10),
                verification_comment: "",
            };

            axios.post(VERIFY_SECTION, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.load_tabs_details();
                        this.show_success("Section verified successfully.")
                    } else {
                        this.show_error('Error during the form submit');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        admin_verify(status) {
            const data = {
                application_form_id: parseInt(this.application_form_id, 10),
                status: status
            };

            axios.post(ADMIN_VERIFY_APPLICATION_URL, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.load_tabs_details();
                        this.show_success("Admin verified successfully.")
                    } else {
                        this.show_error('Error during the admin verification');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        submitAnswers() {
            const formData = new FormData();
            const formatted_answers = [];
            const file_uploads = [];

            const currentForm = this.get_safe_form_config(this.currentFormIndex);
            const currentFormData = this.get_safe_form(this.currentFormIndex);
            const application_form_id = parseInt(this.application_form_id, 10);

            if (!currentForm || !currentFormData) {
                this.show_error("Form data not available");
                return;
            }
            const errors = this.validate_required_fields(currentForm);
            if (Object.keys(errors).length > 0) {
                this.show_info("Please fill in all required fields");
                return;
            }

            // Collect answers for ALL questions (including dependent ones)
            currentForm.sections.forEach(section => {
                section.fields.forEach(field => {
                    // Process main question
                    this.process_question_answer(
                        field,
                        currentFormData[field.model],
                        formatted_answers,
                        file_uploads
                    );

                    // Process dependent questions if they exist and are visible
                    if (field.dependent_questions && field.dependent_questions.length) {
                        field.dependent_questions.forEach(dq => {
                            if (dq.is_visible) {
                                this.process_question_answer(
                                    dq,
                                    currentFormData[dq.model],
                                    formatted_answers,
                                    file_uploads,
                                    true
                                );
                            }
                        });
                    }
                });
            });

            // Track if we have file uploads for emitting event later
            const hasFileUploads = file_uploads.length > 0;

            // Add files to FormData with proper keys
            file_uploads.forEach(file_upload => {
                formData.append(`file_${file_upload.section_question_id}`, file_upload.file);
            });

            // Add other data to FormData
            formData.append('answers', JSON.stringify(formatted_answers));
            formData.append('application_form_id', application_form_id);

            // Submit data using promise chain
            axios.post(SUBMIT_APPLICATION_FORMS_ANSWERS, formData, {
                headers: {
                    ...this.headers,
                    'Content-Type': 'multipart/form-data'
                },
            })
                .then(response => {
                    if (response.data?.status === 'success') {
                        this.show_success('Answers submitted successfully');

                        // Emit event if files were uploaded to trigger document management reload
                        if (hasFileUploads) {
                            this.$emit('files-saved');
                        }

                        // Reload to get updated data
                        this.load_tabs_details();
                    } else {
                        this.show_error(response.data?.message || 'Submission failed');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    } else {
                        this.show_error(error.message || 'An unexpected error occurred');
                    }
                });
        },

        process_question_answer(question, answerValue, formatted_answers, file_uploads, isDependent = false) {
            const section_question_id = isDependent
                ? question.section_question_id // Use the section_question_id from dependent question
                : question.id; // Use the id from main question

            if (!section_question_id) {
                return;
            }

            // Handle file uploads
            if (question.type === QUESTION_TYPES.FILE || question.type === QUESTION_TYPES.FACIAL_VERIFICATION) {
                if (answerValue?.file) {
                    // New file uploaded
                    file_uploads.push({
                        file: answerValue.file,
                        section_question_id: section_question_id
                    });
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: answerValue.file.name
                    });
                } else if (answerValue?.original_url) {
                    // Existing file (already uploaded)
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: answerValue.original_url
                    });
                } else {
                    // File removed or not provided
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: ''
                    });
                }
            }
            // Handle multi-select
            else if (question.type === QUESTION_TYPES.MULTI_SELECT) {
                const answerText = Array.isArray(answerValue)
                    ? answerValue.join(',')
                    : '';
                formatted_answers.push({
                    section_question_id: section_question_id,
                    answer_text: answerText
                });
            }
            // Handle other field types
            else {
                let answerText = answerValue ? answerValue.toString().trim() : '';
                formatted_answers.push({
                    section_question_id: section_question_id,
                    answer_text: answerText
                });
            }
        },

        reset_request_information_state() {
            this.request_info_note = "";
            this.selected_request_fields = [];
            this.answered_file_questions = [];
            this.requested_field_ids = [];
        },

        prepare_request_information(fIdx, sIdx) {
            this.reset_request_information_state();
            this.populate_answered_file_questions(fIdx, sIdx);
        },

        async request_information_submit() {
            if (!this.validate_request_information()) return;

            const application_form_id = this.application_form_id
            var message = this.request_info_note
            var requested_fields = this.selected_request_fields

            const data = {
                application_form_id: application_form_id,
                message: message,
                requested_fields: requested_fields,
            };

            axios.post(REQUEST_INFO_URL, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.close_modal('request_information_modal')
                        this.show_success("Information successfully requested")
                        this.request_info_note = "";
                        this.requested_field_ids = [];
                        this.selected_request_fields = [];
                        this.answered_file_questions = [];
                    } else {
                        this.show_error('Error during the form submit');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        validate_request_information() {
            if (!validate_non_empty_string(this.request_info_note)) {
                this.show_error('Request information note must not be null');
                return false;
            }
            if (!validate_non_sub_zero_number(parseInt(this.application_form_id, 10))) {
                this.show_error('Application form id must not be null');
                return false;
            }
            return true;
        },

        update_dependent_questions_visibility(field, current_value) {
            if (!field.dependent_questions || !field.dependent_questions.length) return;

            field.dependent_questions.forEach(dq => {
                const should_be_visible = this.does_value_match_trigger(current_value, dq.trigger_condition, field);
                dq.is_visible = should_be_visible;
                if (!should_be_visible) {
                    const safeForm = this.get_safe_form(this.currentFormIndex);
                    if (safeForm && Reflect.has(safeForm, dq.model)) {
                        Reflect.set(safeForm, dq.model, dq.__default);
                    }
                }
            });
        },

        does_value_match_trigger(value, triggerCondition, field) {
            if (!triggerCondition) return false;
            if (Array.isArray(value)) {
                const selectedValues = value.map(val => String(val).trim().toLowerCase());
                const trigger = String(triggerCondition).trim().toLowerCase();

                return selectedValues.includes(trigger) ||
                    selectedValues.some(val => val === trigger);
            }

            // Handle single values
            let valueToCompare = value;

            // If it's a select field, try to find the corresponding option text
            if (field.options && field.options.length && value !== null && value !== undefined) {
                const option = field.options.find(opt =>
                    String(opt.value).trim().toLowerCase() === String(value).trim().toLowerCase()
                );
                if (option) {
                    valueToCompare = option.text;
                }
            }

            // Compare normalized values
            const normalizedValue = String(valueToCompare || '').trim().toLowerCase();
            const normalizedTrigger = String(triggerCondition || '').trim().toLowerCase();

            return normalizedValue === normalizedTrigger;
        },

        normalize_value(value) {
            if (value === null || value === undefined) return '';
            return String(value).trim().toLowerCase();
        },

        validate_required_fields(form) {
            const errors = {};
            const formData = this.get_safe_form(this.currentFormIndex);

            if (!form || !Array.isArray(form.sections)) {
                return errors;
            }

            form.sections.forEach(section => {
                (section.fields || []).forEach(field => {
                    const value = Reflect.get(formData, field.model);

                    if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
                        Reflect.set(errors, field.model, `This field is mandatory`);
                    }

                    if (field.dependent_questions && field.dependent_questions.length) {
                        field.dependent_questions.forEach(dq => {
                            const dqValue = Reflect.get(formData, dq.model);

                            if (dq.is_visible && dq.required && (!dqValue || (Array.isArray(dqValue) && dqValue.length === 0))) {
                                Reflect.set(errors, dq.model, `Field required "${dq.label || 'this field'}"`);
                            }
                        });
                    }
                });
            });

            return errors;
        }
    },
};